import { Box, Link, Typography } from "@mui/material";
import type { NikolaGameReference, NikolaSourceCitation } from "@chessla/shared";
import { Link as RouterLink } from "react-router-dom";
import { gameReviewUrl } from "../messaging/chat-handoff";

const UUID = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
const HANDLE = /\b(?:memory|web)-\d+\b/gi;
const PROVIDER = /\b(?:anthropic|bedrock|deepseek|kimi|stockfish)\b/gi;
const TECHNICAL_LABEL = /\b(?:anthropic|bedrock|deepseek|kimi|stockfish|internal)\b/i;

const safeLabel = (label: string, fallback: string): string => {
  if (TECHNICAL_LABEL.test(label)) return fallback;
  const safe = label
    .replace(UUID, "")
    .replace(HANDLE, "")
    .replace(PROVIDER, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  return safe && !/^(?:game|source|id)\s*[:#-]?$/i.test(safe) ? safe : fallback;
};

const gameLabel = (game: NikolaGameReference): string => {
  const opponent = safeLabel(game.opponent ?? "", "this game");
  const move = game.moveNumber ?? (game.ply ? Math.ceil(game.ply / 2) : undefined);
  return [`Game against ${opponent}`, move ? `Move ${move}` : null, game.san ?? null].filter(Boolean).join(" · ");
};

const externalUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" ? parsed.toString() : null;
  } catch {
    return null;
  }
};

interface SourceItem {
  key: string;
  label: string;
  href?: string;
  external?: boolean;
}

const sourceItem = (source: NikolaSourceCitation, index: number): SourceItem => {
  if (source.kind === "memory" && source.game) {
    return {
      key: `game-${index}`,
      label: source.game.opponent ? gameLabel(source.game) : safeLabel(source.label, gameLabel(source.game)),
      href: gameReviewUrl(source.game),
    };
  }
  if (source.kind === "memory") {
    return {
      key: `memory-${index}`,
      label: safeLabel(source.label, "Coaching memory"),
      href: source.sessionId ? `/chat?sessionId=${encodeURIComponent(source.sessionId)}` : undefined,
    };
  }
  if (source.kind === "chessla" || !("url" in source)) {
    return { key: `chessla-${index}`, label: safeLabel(source.label, "Chessla guide") };
  }
  return {
    key: `web-${index}`,
    label: safeLabel(source.label, "Chess source"),
    href: externalUrl(source.url) ?? undefined,
    external: true,
  };
};

const group = (source: NikolaSourceCitation): string => {
  if (source.kind === "memory") return source.game ? "YOUR GAMES" : "YOUR COACHING MEMORY";
  if (source.kind === "chessla") return "CHESSLA";
  return "WEB SOURCES";
};

export const NikolaSources = ({ sources }: { sources: readonly NikolaSourceCitation[] }) => {
  const groups = new Map<string, SourceItem[]>();
  sources.forEach((source, index) =>
    groups.set(group(source), [...(groups.get(group(source)) ?? []), sourceItem(source, index)]),
  );
  if (!groups.size) return null;

  return (
    <Box component="aside" aria-label="Sources" sx={{ mt: 1.5, display: "grid", gap: 1 }}>
      {[...groups.entries()].map(([title, items]) => (
        <Box key={title}>
          <Typography
            sx={{ mb: 0.35, color: "text.secondary", fontSize: ".54rem", fontWeight: 950, letterSpacing: ".12em" }}
          >
            {title}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
            {items.map((item) => {
              const sx = {
                px: 0.85,
                py: 0.55,
                border: "1px solid",
                borderColor: "divider",
                color: "text.primary",
                fontSize: ".68rem",
                fontWeight: 750,
                lineHeight: 1.25,
                minHeight: 44,
                maxWidth: "100%",
                minWidth: 0,
                overflowWrap: "anywhere",
                display: "inline-flex",
                alignItems: "center",
                textDecoration: "none",
                "&:hover": { borderColor: "primary.main", color: "primary.main" },
                "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
              } as const;
              return item.href && item.external ? (
                <Link key={item.key} href={item.href} target="_blank" rel="noopener noreferrer" sx={sx}>
                  {item.label}
                </Link>
              ) : item.href ? (
                <Link key={item.key} component={RouterLink} to={item.href} sx={sx}>
                  {item.label}
                </Link>
              ) : (
                <Box key={item.key} component="span" sx={sx}>
                  {item.label}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
